/**
 * Helpers das permissões de rota do portal do cliente.
 *
 * O objeto do usuário logado (`localStorage.userCliente`) carrega as rotas
 * liberadas em `RlPermissoesUsuarioCliente[].permissao.permissoesVisualizacao[]`,
 * cada uma com `path`, `parentId` e `posicao`.
 */
import apiCliente from "../services/apiCliente";

/**
 * Achata as rotas liberadas em um mapa { path: true }.
 */
export function formatObjectPermissoesCliente(usuarioCliente) {
  const todasVisualizacoes = (
    usuarioCliente?.RlPermissoesUsuarioCliente || []
  ).flatMap((item) => item?.permissao?.permissoesVisualizacao || []);

  return todasVisualizacoes.reduce((acc, viz) => {
    if (viz?.path) {
      acc[viz.path] = true;
    }
    return acc;
  }, {});
}

/**
 * Permissões do usuário logado lidas direto do `localStorage`.
 *
 * Serve como estado inicial síncrono do menu: sem isso a sidebar renderiza
 * vazia até a resposta do backend chegar.
 */
export function lerPermissoesDoLocalStorage() {
  try {
    const data = localStorage.getItem("userCliente");
    if (!data) return null;

    const usuarioCliente = JSON.parse(data)?.usuarioCliente;
    if (!usuarioCliente) return null;

    const admPermissao = (
      usuarioCliente?.RlPermissoesUsuarioCliente || []
    )?.some(
      (item) => item?.permissao?.codigoPermissao?.toUpperCase() === "ADM",
    );

    return {
      admPermissao,
      routesPermissao: formatObjectPermissoesCliente(usuarioCliente),
    };
  } catch (error) {
    return null;
  }
}

/**
 * Monta a árvore das rotas liberadas, ordenada por `posicao` — mesma ordem do
 * menu lateral.
 */
export function buildTreePermissaoCliente(usuarioCliente) {
  const todasVisualizacoes = (
    usuarioCliente?.RlPermissoesUsuarioCliente || []
  ).flatMap((item) => item?.permissao?.permissoesVisualizacao || []);

  const construir = (parentId = null) =>
    todasVisualizacoes
      .filter((item) => item?.parentId === parentId)
      .map((item) => ({ ...item, children: construir(item?.id) }))
      .sort((a, b) => a?.posicao - b?.posicao);

  return construir(null);
}

// Primeiro nó navegável de uma árvore ordenada por posicao: desce para a
// primeira aba quando o nó é só layout (ex.: "/home")
const primeiraRotaDaArvore = (tree) => {
  const primeiro = tree?.[0];
  if (!primeiro) return null;

  return primeiro?.children?.length > 0
    ? primeiro?.children?.[0]?.path
    : primeiro?.path;
};

/**
 * Primeira rota que o usuário pode acessar, seguindo a ordem do menu.
 * Usada no login para não cair na tela de "acesso negado" quando o usuário não
 * tem permissão (ou a rota foi desabilitada pelo interno) para a primeira página.
 */
export async function resolvePrimeiraRotaCliente(usuarioCliente, isAdmin = false) {
  if (isAdmin) {
    // ADM não tem rotas vinculadas por permissão (o bypass é só um flag), então
    // não dá pra usar a árvore de RlPermissoesUsuarioCliente aqui. A única fonte
    // que reflete o que o interno habilitou globalmente é a árvore de rotas do
    // portal em si — a mesma que a tela de criação de permissões usa.
    try {
      const response = await apiCliente.get("/cliente/find/allRoutes");
      return primeiraRotaDaArvore(response?.data) || "/resumo";
    } catch (error) {
      return "/resumo";
    }
  }

  const tree = buildTreePermissaoCliente(usuarioCliente);
  return primeiraRotaDaArvore(tree) || "/resumo";
}
